import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, ElementRef, viewChild, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ProgressService } from '../../services/progress.service';
import { OnboardingService } from '../../services/onboarding.service';
import { countryArray } from '../../../../../shared/constants/SampleData';
import { Web } from 'src/app/shared/constants/AppConstats';
import { ButtonComponent } from 'src/app/shared/components/button/button.component';
import { ImageCropperDialogComponent } from '../image-cropper-dialog/image-cropper-dialog.component';
import { EMAIL_FORMAT_REGEX } from 'src/app/shared/util/validation.constants';
import { ImageUploadService } from 'src/app/shared/services/image-upload.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-additional-club-details',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, InputTextModule, SelectModule, ButtonComponent, ImageCropperDialogComponent],
  templateUrl: './additional-club-details.component.html',
  styleUrls: ['./additional-club-details.component.scss'],
})
export class AdditionalClubDetailsComponent implements OnInit, OnDestroy {
  protected readonly toastr = inject(ToastrService);
  protected readonly router = inject(Router);
  protected readonly progressService = inject(ProgressService);
  protected readonly onboardingService = inject(OnboardingService);
  protected readonly imageUpload = inject(ImageUploadService);
  protected readonly destroy$ = new Subject<void>();

  protected clubInformation: any;
  protected adminInformation: any;
  protected activityId = signal<string>('');
  protected activitiesLoaded = signal(false);
  protected submitted = signal(false);
  protected emailsMatch = signal(false);

  protected appIconUrl = signal<string>('');
  protected croppedImage = signal<string | undefined>(undefined);
  protected imageBase64 = signal<string | undefined>(undefined);
  protected showCropperDialog = signal(false);
  protected selectedFile = signal<File | undefined>(undefined);
  protected uploadingLogo = signal(false);

  readonly fileInput = viewChild.required<ElementRef>('fileInput');

  protected countries: any[] = [];
  protected gendersDropdown = [
    { label: 'Male', value: 'Male' },
    { label: 'Female', value: 'Female' },
    { label: 'Others', value: 'Others' },
  ];

  private static readonly MAX_LOGO_BYTES = 5 * 1024 * 1024;
  private static readonly ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg'];
  private static readonly ALLOWED_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg'];


  ngOnInit(): void {
    this.clubInformation = this.onboardingService.getAccountInformation().clubInformation;
    this.adminInformation = this.onboardingService.getAccountInformation().adminInformation;
    this.appIconUrl.set(this.onboardingService.getAccountInformation().appIconUrl || '');
    this.croppedImage.set(this.onboardingService.getAccountInformation().croppedImage || '');

    this.countries = countryArray.reduce((acc: any[], region: any) => acc.concat(region.items), []);

    if (this.clubInformation.clubAddress.country && !this.clubInformation.clubAddress.countryName) {
      const found = this.countries.find((c: any) => c.code === this.clubInformation.clubAddress.country);
      if (found) this.clubInformation.clubAddress.countryName = found.label;
    }

    if (this.adminInformation.primaryActivityId) {
      this.activityId.set(this.adminInformation.primaryActivityId);
      this.activitiesLoaded.set(true);
    } else {
      this.toastr.error('Activity selection not found. Please go back and select an activity.');
    }

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe(() => window.scroll({ top: 0, left: 0, behavior: 'auto' }));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected onSubmit(): void {
    this.submitted.set(true);

    const match = this.clubInformation.coachDetails.email === this.adminInformation.email;
    this.emailsMatch.set(match);

    const firstInvalidId = this.getFirstInvalidFieldId();
    if (firstInvalidId) {
      this.toastr.error('Please correct the highlighted fields');
      this.focusFirstInvalid(firstInvalidId);
      return;
    }

    // The logo upload is async and its URL goes into the create-club payload. Leaving
    // early would submit an empty `app_icon_url`, and the backend silently substitutes
    // a generic default icon, so the club is created with the wrong logo and no error.
    if (this.uploadingLogo()) {
      this.toastr.info('Please wait for the logo upload to finish');
      return;
    }

    if (this.clubInformation.coachDetails.email) {
      this.clubInformation.coachDetails.email = this.clubInformation.coachDetails.email.replace(/^mailto:/i, '');
    }

    // Check coach email uniqueness
    this.onboardingService.checkEmailUnique(this.clubInformation.coachDetails.email)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res?.data?.unique === false || res?.unique === false) {
            this.toastr.error(res?.data?.message || res?.message || 'Coach email already in use');
            return;
          }
          this.router.navigate(['/auth/onboarding/review-submit']);
        },
        error: () => {
          // If check fails, proceed anyway — backend will catch it
          this.router.navigate(['/auth/onboarding/review-submit']);
        }
      });
  }

  private getFirstInvalidFieldId(): string | null {
    if (!this.clubInformation.clubAddress.country) { return 'inputCountrySelect'; }
    if (!this.clubInformation.clubAddress.address?.trim()) { return 'inputAddress'; }
    if (!this.clubInformation.clubAddress.state?.trim()) { return 'inputState'; }
    if (!this.clubInformation.clubAddress.postCode?.trim()) { return 'inputZip'; }
    if (!this.clubInformation.clubTnC?.trim()) { return 'clubTnC'; }
    if (!this.isValidUrl(this.clubInformation.clubTnC)) { return 'clubTnC'; }
    if (!this.clubInformation.coachDetails.firstName?.trim()) { return 'firstName'; }
    if (!this.clubInformation.coachDetails.lastName?.trim()) { return 'lastName'; }
    if (!this.clubInformation.coachDetails.email?.trim()) { return 'coachEmail'; }
    if (!this.isValidEmailFormat(this.clubInformation.coachDetails.email)) { return 'coachEmail'; }
    if (this.clubInformation.coachDetails.email === this.adminInformation.email) { return 'coachEmail'; }
    if (!this.clubInformation.coachDetails.gender) { return 'genderSelect'; }
    return null;
  }

  private focusFirstInvalid(id: string): void {
    setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) { return; }
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
    }, 0);
  }

  protected isValidEmailFormat(v: string | undefined | null): boolean {
    return !!v && EMAIL_FORMAT_REGEX.test(v);
  }

  protected isValidUrl(v: string): boolean {
    if (!v) { return false; }
    const urlPattern = /^https?:\/\/[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+(:\d+)?([/?#][^\s]*)?$/i;
    return urlPattern.test(v.trim());
  }

  protected onCountryChange(event: any): void {
    const found = this.countries.find((c: any) => c.code === event.value);
    if (found) {
      this.clubInformation.currency = found.currency.code;
      this.clubInformation.clubAddress.countryName = found.label;
    }
  }

  protected handleCropConfirm(croppedBase64: string): void {
    this.croppedImage.set(croppedBase64);
    this.showCropperDialog.set(false);
    this.onboardingService.accountInformation.croppedImage = croppedBase64;

    const base64Data = croppedBase64.split(',')[1];
    const blob = this.base64ToBlob(base64Data);
    const fileName = this.selectedFile()?.name?.replace(/\.[^.]+$/, '') ?? 'club-icon';

    // Upload browser -> S3 directly via a presigned URL, the same mechanism every
    // other image upload in the app uses (ImageUploadService: fnb, shop, promotions,
    // case attachments). Posting the file as multipart to the API instead corrupts it:
    // the secured gateway decodes the binary body as UTF-8, so every byte above 0x7F
    // becomes U+FFFD (EF BF BD) and the stored PNG can no longer be decoded by any
    // browser, even though S3 serves it with a 200.
    this.uploadingLogo.set(true);
    this.imageUpload.getPresignedUrl$(`${fileName}_${Date.now()}.png`, 'parentclub_logo', 'parentclub_logo')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (urls) => {
          const presignedUrl = urls?.[0]?.url;
          if (!presignedUrl) {
            this.uploadingLogo.set(false);
            this.toastr.error('Failed to upload image');
            this.removeUploadedImage();
            return;
          }
          this.imageUpload.uploadToS3$(presignedUrl, blob, 'image/png')
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                const objectPath = new URL(presignedUrl).pathname;
                const publicUrl = `${environment.aws.cloudfrontURL}${objectPath}`;
                this.appIconUrl.set(publicUrl);
                this.onboardingService.accountInformation.appIconUrl = publicUrl;
                this.uploadingLogo.set(false);
                this.toastr.success('Image Uploaded Successfully');
              },
              error: () => {
                this.uploadingLogo.set(false);
                this.toastr.error('Failed to upload image');
                this.removeUploadedImage();
              }
            });
        },
        error: () => {
          this.uploadingLogo.set(false);
          this.toastr.error('Failed to upload image');
          this.removeUploadedImage();
        }
      });
    this.imageBase64.set(undefined);
  }

  protected handleCropCancel(): void {
    this.showCropperDialog.set(false);
    this.imageBase64.set(undefined);
  }

  protected onHiddenFileSelect(eventOrFile: any): void {
    let file: File | undefined;
    let input: HTMLInputElement | undefined;
    if (eventOrFile instanceof File) {
      file = eventOrFile;
    } else if (eventOrFile?.target?.files?.length) {
      input = eventOrFile.target as HTMLInputElement;
      file = input.files![0];
    }
    if (!file) { return; }

    const nameLower = file.name.toLowerCase();
    const hasAllowedExtension = AdditionalClubDetailsComponent.ALLOWED_IMAGE_EXTENSIONS.some(ext => nameLower.endsWith(ext));
    const hasAllowedType = AdditionalClubDetailsComponent.ALLOWED_IMAGE_TYPES.includes(file.type);

    if (!hasAllowedType || !hasAllowedExtension) {
      this.toastr.error('Only PNG or JPEG images are allowed.');
      if (input) { input.value = ''; }
      return;
    }

    if (file.size > AdditionalClubDetailsComponent.MAX_LOGO_BYTES) {
      this.toastr.error('Image must be smaller than 5 MB.');
      if (input) { input.value = ''; }
      return;
    }

    if (input) { input.value = ''; }

    this.selectedFile.set(file);
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.imageBase64.set(e.target.result as string);
      this.showCropperDialog.set(true);
      this.croppedImage.set(undefined);
    };
    reader.readAsDataURL(file);
  }

  protected removeUploadedImage(): void {
    this.croppedImage.set(undefined);
    this.appIconUrl.set('');
    this.selectedFile.set(undefined);
  }

  protected triggerFileInput(): void {
    this.fileInput().nativeElement.click();
  }

  protected goBack(): void {
    this.router.navigate(['/auth/onboarding/create-account']);
  }

  private base64ToBlob(base64Data: string, contentType = 'image/png'): Blob {
    const sliceSize = 512;
    const byteCharacters = atob(base64Data);
    const byteArrays: Uint8Array[] = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) byteNumbers[i] = slice.charCodeAt(i);
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    return new Blob(byteArrays as BlobPart[], { type: contentType });
  }
}
